import { api } from './api';
import {
  TransactionQueue,
  TransactionQueueWithUser,
  TransactionQueueFilters,
  TransactionQueueListResponse,
  GenerateTransactionsRequest,
  GenerateTransactionsResponse,
  BulkApprovalRequest,
  BulkApprovalResponse,
  BulkRejectionRequest,
  BulkRejectionResponse,
  RetryTransactionRequest,
  RetryTransactionResponse,
  TransactionQueueStats,
  StandingOrder,
} from '@mygate/common';

class TransactionQueueService {
  /**
   * Fetch transaction queue with filters
   */
  async fetchTransactionQueue(filters?: TransactionQueueFilters) {
    const params = new URLSearchParams();

    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        filters.status.forEach(s => params.append('status', s));
      } else {
        params.append('status', filters.status);
      }
    }

    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.standingOrderId) params.append('standingOrderId', filters.standingOrderId);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.minAmount) params.append('minAmount', filters.minAmount.toString());
    if (filters?.maxAmount) params.append('maxAmount', filters.maxAmount.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const endpoint = `/api/staff/transaction-queue${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<TransactionQueueListResponse>(endpoint);
    return response.data;
  }

  /**
   * Get transaction by ID
   */
  async getTransaction(id: string) {
    const response = await api.get<TransactionQueueWithUser>(`/api/staff/transaction-queue/${id}`);
    return response.data;
  }

  /**
   * Generate transactions from standing orders
   */
  async generateTransactions(request: GenerateTransactionsRequest) {
    const response = await api.post<GenerateTransactionsResponse>('/api/staff/transaction-queue/generate', request);
    return response.data;
  }

  /**
   * Approve a single transaction
   */
  async approveTransaction(id: string) {
    const response = await api.put<TransactionQueue>(`/api/staff/transaction-queue/${id}/approve`);
    return response.data;
  }

  /**
   * Reject a single transaction
   */
  async rejectTransaction(id: string, reason: string) {
    const response = await api.put<TransactionQueue>(`/api/staff/transaction-queue/${id}/reject`, { reason });
    return response.data;
  }

  /**
   * Cancel a transaction
   */
  async cancelTransaction(id: string, reason: string) {
    const response = await api.put<TransactionQueue>(`/api/staff/transaction-queue/${id}/cancel`, { reason });
    return response.data;
  }

  /**
   * Retry a failed transaction
   */
  async retryTransaction(id: string, reason?: string) {
    const request: RetryTransactionRequest = reason ? { reason } : {};
    const response = await api.put<RetryTransactionResponse>(`/api/staff/transaction-queue/${id}/retry`, request);
    return response.data;
  }

  /**
   * Bulk approve transactions
   */
  async bulkApprove(request: BulkApprovalRequest) {
    const response = await api.post<BulkApprovalResponse>('/api/staff/transaction-queue/bulk-approve', request);
    return response.data;
  }

  /**
   * Bulk reject transactions
   */
  async bulkReject(request: BulkRejectionRequest) {
    const response = await api.post<BulkRejectionResponse>('/api/staff/transaction-queue/bulk-reject', request);
    return response.data;
  }

  /**
   * Get transaction queue statistics
   */
  async getStatistics() {
    const response = await api.get<TransactionQueueStats>('/api/staff/transaction-queue/statistics');
    return response.data;
  }

  /**
   * Fetch standing orders
   */
  async fetchStandingOrders(filters?: {
    status?: string;
    isPaused?: string;
    userId?: string;
    type?: string;
  }) {
    const params = new URLSearchParams();

    if (filters?.status) params.append('status', filters.status);
    if (filters?.isPaused) params.append('isPaused', filters.isPaused);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.type) params.append('type', filters.type);

    const queryString = params.toString();
    const endpoint = `/api/staff/standing-orders${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<StandingOrder[]>(endpoint);
    return response.data;
  }

  /**
   * Pause a standing order
   */
  async pauseStandingOrder(id: string, reason?: string) {
    const response = await api.put<StandingOrder>(`/api/staff/standing-orders/${id}/pause`, { reason });
    return response.data;
  }

  /**
   * Resume a standing order
   */
  async resumeStandingOrder(id: string, reason?: string) {
    const response = await api.put<StandingOrder>(`/api/staff/standing-orders/${id}/resume`, { reason });
    return response.data;
  }

  /**
   * Export transactions to CSV
   * TODO: Implement streaming CSV export for large datasets to prevent memory issues
   */
  exportToCSV(transactions: TransactionQueue[]): string {
    const headers = [
      'ID',
      'Status',
      'Due Date',
      'Recipient',
      'IBAN',
      'Amount',
      'Purpose',
      'Standing Order ID',
      'Approved By',
      'Approved At',
      'Processed At',
      'Completed At',
      'Failure Reason',
    ];

    const rows = transactions.map(t => [
      t.id,
      t.status,
      new Date(t.dueDate).toLocaleDateString(),
      t.recipient,
      t.iban,
      t.amount,
      t.purpose,
      t.standingOrderId,
      t.approvedBy || '',
      t.approvedAt ? new Date(t.approvedAt).toLocaleString() : '',
      t.processedAt ? new Date(t.processedAt).toLocaleString() : '',
      t.completedAt ? new Date(t.completedAt).toLocaleString() : '',
      t.failureReason || '',
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');

    return csvContent;
  }

  /**
   * Download CSV file
   */
  downloadCSV(transactions: TransactionQueue[], filename: string = 'transaction-queue.csv') {
    const csv = this.exportToCSV(transactions);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export const transactionQueueService = new TransactionQueueService();
